import type { CSSProperties } from 'react';
import { formatDate } from '../utils/formatters';

// Helper function to get frequency label from days
function getFrequencyLabel(days: number | string | null | undefined) {
  if (!days) return 'Not set';
  const d = parseInt(String(days));
  if (d >= 365) return '1x/year';
  if (d >= 180) return '2x/year';
  if (d >= 120) return '3x/year';
  if (d >= 90) return '4x/year';
  if (d >= 30) return 'Monthly';
  return `${d} days`;
}

interface HospitalityFormData {
  is_pos: boolean;
  hospitality_notes: string;
  [key: string]: unknown;
}

interface CustomerProperty {
  id: string;
  property_name: string;
  total_rooms?: number | null;
  hospitality_tier?: string | null;
  default_service_frequency_days?: number | string | null;
  contract_end_date?: string | null;
}

interface CustomerFormHospitalitySectionProps {
  expanded: boolean;
  onToggle: () => void;
  formData: HospitalityFormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  selectedCustomer: { id?: string } | null;
  customerProperties: CustomerProperty[];
  loadingProperties: boolean;
  styles: Record<string, CSSProperties>;
}

export default function CustomerFormHospitalitySection({
  expanded,
  onToggle,
  formData,
  onChange,
  selectedCustomer,
  customerProperties,
  loadingProperties,
  styles,
}: CustomerFormHospitalitySectionProps) {
  return (
    <div style={styles.section}>
      <div
        style={styles.collapsibleHeader}
        onClick={onToggle}
      >
        <span style={styles.sectionTitle}>
          {expanded ? '▼' : '▶'} Peace of Sleep
        </span>
        <span style={styles.expandHint}>
          {expanded ? 'Collapse' : 'Expand'}
        </span>
      </div>

      {expanded && (
        <div style={styles.collapsibleContent}>
          {/* Configuration Section */}
          <div style={styles.posSubsection}>
            <div style={styles.posSubsectionTitle}>Peace of Sleep Configuration</div>
            <div style={styles.checkboxRow}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="is_pos"
                  checked={formData.is_pos}
                  onChange={onChange}
                />
                <span className="fw-500">This is a Peace of Sleep customer</span>
              </label>
            </div>
            <div style={styles.helpText}>
              Enable to manage properties under the Peace of Sleep programme
            </div>
          </div>

          {formData.is_pos && (
            <>
              {/* Properties Section */}
              <div style={styles.posSubsection}>
                <div style={styles.posSubsectionTitle}>Properties</div>
                <div style={styles.propertiesList}>
                  {loadingProperties ? (
                    <div style={styles.loadingText}>Loading properties...</div>
                  ) : customerProperties.length === 0 ? (
                    <div style={styles.noPropertiesText}>
                      {selectedCustomer?.id
                        ? 'No properties configured yet.'
                        : 'Save customer first, then add properties in Hospitality Management.'}
                    </div>
                  ) : (
                    customerProperties.map(prop => (
                      <div key={prop.id} style={styles.propertyItem}>
                        <div style={styles.propertyInfo}>
                          <div style={styles.propertyName}>{prop.property_name}</div>
                          <div style={styles.propertyMeta}>
                            <span>{prop.total_rooms || 0} rooms</span>
                            <span style={{
                              ...styles.tierBadge,
                              ...(prop.hospitality_tier === 'signature' ? styles.tierSignature :
                                 prop.hospitality_tier === 'assured' ? styles.tierAssured :
                                 styles.tierDistinguished)
                            }}>
                              {(prop.hospitality_tier || 'distinguished').charAt(0).toUpperCase() +
                               (prop.hospitality_tier || 'distinguished').slice(1)}
                            </span>
                            <span>{getFrequencyLabel(prop.default_service_frequency_days)}</span>
                            <span>Ends: {prop.contract_end_date
                              ? formatDate(prop.contract_end_date)
                              : 'Not set'}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="mt-md">
                  <a
                    href="/hospitality-management"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.manageLink}
                  >
                    Manage in Hospitality Management &rarr;
                  </a>
                </div>
              </div>

              {/* Notes Section */}
              <div style={styles.posSubsection}>
                <div style={styles.posSubsectionTitle}>Notes</div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Peace of Sleep Notes</label>
                  <textarea
                    name="hospitality_notes"
                    style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                    value={formData.hospitality_notes}
                    onChange={onChange}
                    placeholder="Special requirements, account notes, etc."
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
